/**
 * Escuta de barge-in (V2, voz v3, 08/09) — abre um stream de MEDIÇÃO (sem
 * gravar nada) e chama `aoDetectarFala()` quando o VAD de barge-in
 * (`vad-de-voz.js::criarVadDeBargeIn`) dispara. Extraído de `motor-de-voz.js`
 * pra manter o arquivo sob o teto de 500 linhas do repo — só isola a
 * MAQUINARIA de abrir/fechar o stream de escuta; QUEM decide iniciar/parar de
 * escutar continua em `motor-de-voz.js` (que observa `motorDeVoz.estadoDaFala`
 * via `$watch` e chama `iniciar()`/`parar()` deste módulo).
 *
 * [feedback do owner, 08/09] "não conseguia cortar o áudio da IA (precisei
 * apertar para parar)". Sustentando fala acima do limiar de barge-in por
 * `HISTERESE_BARGE_IN_MS`, `aoDetectarFala()` dispara — o CHAMADOR
 * (`motor-de-voz.js`) decide o que fazer (`iniciar(MODO_VOZ, ...)`, que já
 * interrompe a fala da IA no topo).
 *
 * Silencioso em qualquer falha (permissão, hardware, navegador sem suporte):
 * barge-in é um EXTRA sobre o fluxo principal (clique/tecla continuam
 * funcionando) — uma falha aqui não pode quebrar o resto do Modo de Voz.
 */
import {
  pedirMicrofoneComFallback,
  criarMedidorDeNivel,
  liberarStream,
  suportaEntradaDeVoz,
} from './captura-de-voz'
import { criarVadDeBargeIn } from './vad-de-voz'

/**
 * @param {Object} opts
 * @param {Function} opts.aoDetectarFala Chamado quando o VAD detecta barge-in.
 * @param {() => boolean} opts.aindaValido Confere, DEPOIS do `await` de pedir
 *   o mic, se a escuta ainda faz sentido (a IA pode ter parado de falar, ou
 *   a sessão pode ter encerrado, enquanto o `getUserMedia` estava em voo).
 * @returns {{iniciar: () => Promise<void>, parar: () => void}}
 */
export const criarEscutaDeBargeIn = ({ aoDetectarFala, aindaValido }) => {
  let _pararStream = null

  const parar = () => {
    if (!_pararStream) return
    _pararStream()
    _pararStream = null
  }

  const iniciar = async () => {
    if (_pararStream) return // já escutando
    if (!suportaEntradaDeVoz()) return
    let stream
    try {
      stream = await pedirMicrofoneComFallback()
    } catch (erro) {
      return
    }
    if (!aindaValido()) {
      stream.getTracks().forEach((track) => track.stop())
      return
    }
    const medidor = criarMedidorDeNivel(stream)
    const vad = criarVadDeBargeIn({
      aoDetectarFala: () => {
        parar()
        aoDetectarFala()
      },
    })
    medidor.iniciar((rms) => vad.avaliar(rms))
    _pararStream = () => {
      medidor.parar()
      liberarStream(stream)
    }
  }

  return { iniciar, parar }
}
